import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  User,
  Conversation,
  Message,
  AIResponse,
  LoginCredentials,
  RegisterData,
  AuthTokens,
  AnalyticsOverview,
  AgentPerformance,
  BlockedIp
} from '../types';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

class APIService {
  private client: AxiosInstance;
  private tokens: AuthTokens | null = null;
  private refreshTokenPromise: Promise<AuthTokens> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load tokens from localStorage
    const storedTokens = localStorage.getItem('auth_tokens');
    if (storedTokens && storedTokens !== 'undefined') {
      try {
        this.tokens = JSON.parse(storedTokens);
      } catch (e) {
        // Invalid token data, clear it
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('user');
      }
    }

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.tokens?.accessToken) {
          config.headers.Authorization = `Bearer ${this.tokens.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (this.tokens?.refreshToken) {
            try {
              // If a refresh is already in progress, wait for it
              if (!this.refreshTokenPromise) {
                this.refreshTokenPromise = this.performTokenRefresh();
              }

              const newTokens = await this.refreshTokenPromise;
              this.refreshTokenPromise = null;

              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;

              return this.client(originalRequest);
            } catch (refreshError) {
              this.refreshTokenPromise = null;
              this.clearTokens();
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          } else {
            // No refresh token, redirect to login
            this.clearTokens();
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async performTokenRefresh(): Promise<AuthTokens> {
    const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
      refreshToken: this.tokens!.refreshToken,
    });

    this.setTokens(data);
    return data;
  }

  private setTokens(tokens: AuthTokens) {
    this.tokens = tokens;
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  }

  private clearTokens() {
    this.tokens = null;
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('user');
  }

  // Auth methods
  async login(credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> {
    const { data } = await this.client.post('/auth/login', credentials);
    this.setTokens(data.tokens);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  }

  async register(registerData: RegisterData): Promise<{ user: User; tokens: AuthTokens }> {
    const { data } = await this.client.post('/auth/register', registerData);
    this.setTokens(data.tokens);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  }

  async checkWorkspaceAvailability(workspaceId: string): Promise<{ available: boolean; workspaceId: string; error?: string }> {
    const { data } = await this.client.get('/auth/check-workspace', {
      params: { workspaceId }
    });
    return data;
  }

  logout() {
    this.clearTokens();
  }

  isAuthenticated(): boolean {
    return !!this.tokens?.accessToken;
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Conversation methods
  async getConversations(params?: {
    status?: string;
    handling_mode?: string;
    assigned_user_id?: string;
    search?: string;
  }): Promise<Conversation[]> {
    const { data } = await this.client.get('/conversations', { params });
    return data.conversations;
  }

  async getConversation(id: string): Promise<Conversation> {
    const { data } = await this.client.get(`/conversations/${id}`);
    return data.conversation;
  }

  async createConversation(contactData: { name?: string; email?: string }): Promise<Conversation> {
    const { data } = await this.client.post('/conversations', contactData);
    return data.conversation;
  }

  async escalateConversation(id: string): Promise<Conversation> {
    const { data } = await this.client.patch(`/conversations/${id}/escalate`);
    return data.conversation;
  }

  async takeoverConversation(id: string): Promise<Conversation> {
    const { data } = await this.client.patch(`/conversations/${id}/takeover`);
    return data.conversation;
  }

  async updateConversationStatus(id: string, status: 'open' | 'pending' | 'closed'): Promise<Conversation> {
    const { data } = await this.client.patch(`/conversations/${id}`, { status });
    return data.conversation;
  }

  async closeConversation(id: string): Promise<void> {
    await this.client.patch(`/conversations/${id}/close`);
  }

  async reopenConversation(id: string): Promise<void> {
    await this.client.patch(`/conversations/${id}/reopen`);
  }

  async blockContact(contactId: string, reason?: string): Promise<void> {
    await this.client.post(`/contacts/${contactId}/block`, { reason });
  }

  async unblockContact(contactId: string): Promise<void> {
    await this.client.post(`/contacts/${contactId}/unblock`);
  }

  // Blocked IP methods
  async getBlockedIps(): Promise<BlockedIp[]> {
    const { data } = await this.client.get('/blocked-ips');
    return data.blockedIps;
  }

  async blockIp(params: {
    cidr: string;
    reason?: string;
    expires_in_hours?: number;
  }): Promise<BlockedIp> {
    const { data } = await this.client.post('/blocked-ips', params);
    return data.blockedIp;
  }

  async unblockIp(id: string): Promise<void> {
    await this.client.delete(`/blocked-ips/${id}`);
  }

  // Contacts methods
  async getContacts(params?: {
    search?: string;
    blocked?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    contacts: any[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const { data } = await this.client.get('/contacts', { params });
    return data;
  }

  async getContact(id: string): Promise<any> {
    const { data } = await this.client.get(`/contacts/${id}`);
    return data.contact;
  }

  async exportContacts(params?: { search?: string; blocked?: string }): Promise<Blob> {
    const response = await this.client.get('/contacts/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  async updateContact(id: string, data: { name?: string; email?: string; notes?: string }): Promise<any> {
    const response = await this.client.put(`/contacts/${id}`, data);
    return response.data.contact;
  }

  // Message methods
  async getMessages(conversationId: string): Promise<Message[]> {
    const { data } = await this.client.get(`/conversations/${conversationId}/messages`);
    return data.messages;
  }

  async sendMessage(conversationId: string, content: string): Promise<Message> {
    const { data} = await this.client.post(`/conversations/${conversationId}/messages`, { content });
    return data.message;
  }

  async sendMessageAsEmail(conversationId: string, content: string): Promise<Message> {
    const { data } = await this.client.post(`/email/${conversationId}/send`, { content });
    return data.message;
  }

  async requestAIAssist(conversationId: string, question: string): Promise<AIResponse> {
    const { data } = await this.client.post(`/conversations/${conversationId}/ai-assist`, { question });
    return data;
  }

  // AI Response methods
  async updateAIResponseFeedback(
    responseId: string,
    action: 'approved' | 'edited' | 'rejected',
    editedAnswer?: string
  ): Promise<void> {
    await this.client.patch(`/ai/responses/${responseId}/feedback`, {
      action,
      edited_answer: editedAnswer,
    });
  }

  // User methods
  async updateUserStatus(status: 'online' | 'away' | 'offline'): Promise<User> {
    const user = this.getCurrentUser();
    if (!user) throw new Error('No user logged in');

    const { data } = await this.client.patch(`/users/${user.id}/status`, { status });
    localStorage.setItem('user', JSON.stringify(data.user));
    return data.user;
  }

  async getUsers(): Promise<User[]> {
    const { data } = await this.client.get('/users');
    return data.users;
  }

  // Analytics methods
  async getAnalyticsOverview(): Promise<AnalyticsOverview> {
    const { data } = await this.client.get('/analytics/overview');
    return data;
  }

  async getAgentPerformance(): Promise<AgentPerformance[]> {
    const { data } = await this.client.get('/analytics/agent-performance');
    return data.agents;
  }

  // Knowledge Base methods
  async getKnowledgeArticles(): Promise<any[]> {
    const { data } = await this.client.get('/knowledge/articles');
    return data.articles;
  }

  async getKnowledgeStats(): Promise<any> {
    const { data } = await this.client.get('/knowledge/stats');
    return data; // Return full response (stats, article_limit, plan)
  }

  async crawlUrl(url: string): Promise<any> {
    const { data } = await this.client.post('/knowledge/crawl', { url });
    return data;
  }

  async startFullSiteCrawl(url: string): Promise<{ job_id: string }> {
    const { data } = await this.client.post('/onboarding/crawl', { url });
    return data;
  }

  async getCrawlStatus(jobId: string): Promise<any> {
    const { data } = await this.client.get(`/onboarding/status/${jobId}`);
    return data;
  }

  async deleteArticle(id: string): Promise<void> {
    await this.client.delete(`/knowledge/articles/${id}`);
  }

  async reindexArticle(id: string): Promise<void> {
    await this.client.post(`/knowledge/articles/${id}/reindex`);
  }

  async getKnowledgeGaps(limit: number = 20): Promise<any[]> {
    const { data } = await this.client.get('/knowledge/gaps', { params: { limit } });
    return data.gaps;
  }

  async createQuickEntry(question: string, answer: string): Promise<any> {
    const { data } = await this.client.post('/knowledge/quick-entry', { question, answer });
    return data;
  }

  async uploadDocument(formData: FormData): Promise<any> {
    const { data } = await this.client.post('/knowledge/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  }

  // AI Test methods
  async testAI(question: string): Promise<{ ai_answer: string; confidence_score: number; sources: any[] }> {
    const { data } = await this.client.post('/ai/test', { question });
    return data;
  }

  // Settings methods
  async getProfile(): Promise<User> {
    const { data } = await this.client.get('/settings/profile');
    return data;
  }

  async updateProfile(profileData: { name?: string; email?: string; currentPassword?: string; newPassword?: string }): Promise<void> {
    await this.client.patch('/settings/profile', profileData);
  }

  async uploadProfilePicture(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('profilePicture', file);
    const { data } = await this.client.post('/settings/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  }

  async getOrganizationSettings(): Promise<any> {
    const { data } = await this.client.get('/settings/organization');
    return data;
  }

  async updateOrganizationSettings(settings: any): Promise<void> {
    await this.client.patch('/settings/organization', settings);
  }

  // Crawl Rules methods
  async getCrawlRules(): Promise<any> {
    const { data } = await this.client.get('/crawl-rules');
    return data;
  }

  async createCrawlRule(ruleType: string, ruleValue: string): Promise<any> {
    const { data } = await this.client.post('/crawl-rules', { rule_type: ruleType, rule_value: ruleValue });
    return data;
  }

  async updateCrawlRule(ruleId: string, updates: { rule_value?: string; enabled?: boolean }): Promise<any> {
    const { data } = await this.client.patch(`/crawl-rules/${ruleId}`, updates);
    return data;
  }

  async deleteCrawlRule(ruleId: string): Promise<void> {
    await this.client.delete(`/crawl-rules/${ruleId}`);
  }

  // Slack Integration methods
  async getSlackStatus(): Promise<{ connected: boolean; team_name?: string; team_id?: string; channel?: string }> {
    const { data } = await this.client.get('/integrations/slack/status');
    return data;
  }

  async initSlackInstall(): Promise<{ success: boolean; auth_url: string }> {
    const { data } = await this.client.get('/integrations/slack/install');
    return data;
  }

  async updateSlackConfig(config: { channel_id: string; channel_name: string }): Promise<void> {
    await this.client.patch('/integrations/slack/config', config);
  }

  async disconnectSlack(): Promise<void> {
    await this.client.delete('/integrations/slack');
  }

  // Orchestrations methods
  async getOrchestrations(): Promise<any[]> {
    const { data } = await this.client.get('/orchestrations');
    return data;
  }

  async getOrchestration(id: string): Promise<any> {
    const { data } = await this.client.get(`/orchestrations/${id}`);
    return data;
  }

  async createOrchestration(orchestration: any): Promise<any> {
    const { data } = await this.client.post('/orchestrations', orchestration);
    return data;
  }

  async updateOrchestration(id: string, orchestration: any): Promise<any> {
    const { data } = await this.client.patch(`/orchestrations/${id}`, orchestration);
    return data;
  }

  async deleteOrchestration(id: string): Promise<void> {
    await this.client.delete(`/orchestrations/${id}`);
  }

  async getGlobalInstructions(): Promise<any> {
    const { data} = await this.client.get('/orchestrations/global/instructions');
    return data;
  }

  async updateGlobalInstructions(instructions: any): Promise<any> {
    const { data } = await this.client.patch('/orchestrations/global/instructions', instructions);
    return data;
  }

  // Onboarding methods
  async getOnboardingStats(): Promise<{
    total_articles: number;
    indexed_articles: number;
    pending_articles: number;
    failed_articles: number;
    total_chunks: number;
  }> {
    const { data } = await this.client.get('/onboarding/stats');
    return data;
  }

  async validateAI(customQuestions?: string[]): Promise<{
    success: boolean;
    results: Array<{
      question: string;
      answer: string;
      confidence: number;
      handling_mode: string;
      sources: Array<{ title: string; url: string }>;
    }>;
  }> {
    const { data } = await this.client.post('/onboarding/validate', { custom_questions: customQuestions });
    return data;
  }

  async activateAI(): Promise<{ success: boolean; message: string }> {
    const { data } = await this.client.post('/onboarding/activate');
    return data;
  }

  // Widget Theme methods
  async getThemes(): Promise<any[]> {
    const { data } = await this.client.get('/widget/themes');
    return data;
  }

  async createTheme(name: string, config: any): Promise<any> {
    const { data } = await this.client.post('/widget/themes', { name, config });
    return data;
  }

  async activateTheme(themeId: string): Promise<any> {
    const { data } = await this.client.patch(`/widget/themes/${themeId}`, { is_active: true });
    return data;
  }

  async extractBrand(url: string): Promise<{
    logo?: string;
    favicon?: string;
    colors: { primary: string; secondary: string; background: string; text: string };
    suggestedTheme: any;
    extractedFrom: string;
  }> {
    const { data } = await this.client.post('/widget/brand/extract', { url });
    return data;
  }

  // Widget Domain methods
  async getDomains(): Promise<any[]> {
    const { data } = await this.client.get('/widget/domains');
    return data;
  }

  async addDomain(host: string): Promise<any> {
    const { data } = await this.client.post('/widget/domains', { host });
    return data;
  }

  async removeDomain(domainId: string): Promise<void> {
    await this.client.delete(`/widget/domains/${domainId}`);
  }
}

export const api = new APIService();
